import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import { trans, uploadDiscussionHeroImage, tagsAreChangelogProducts, changelogAttributes } from '../utils';

/** Contador do menu de rascunhos do fof (`draftCount` do usuário): o delete não devolve o usuário, então desce aqui como no fof. */
const adjustDraftCount = delta => {
  const attributes = app.session.user?.data?.attributes;
  if (!attributes) return;
  attributes.draftCount = Math.max(0, (Number(attributes.draftCount) || 0) + delta);
};

/**
 * Estado do compositor de nova discussão do tema: campos, tags (com o "ignorar
 * requisitos" para quem pode), flags de prévia/envio e o envio em si.
 */
export default class InlineComposerState {
  constructor() {
    _defineProperty(this, "title", '');
    _defineProperty(this, "body", '');
    _defineProperty(this, "tags", []);
    _defineProperty(this, "preview", false);
    /** Tela cheia do cartão flutuante: a prévia, quando ligada, fica ao lado do editor. */
    _defineProperty(this, "fullScreen", false);
    _defineProperty(this, "submitting", false);
    _defineProperty(this, "tagBypassReqs", false);
    _defineProperty(this, "tagPickerOpen", false);
    _defineProperty(this, "tagFilter", '');
    // Imagem do hero opcional, quando uma das tags está em `avocadoHeroImageTags`.
    // Fica só no cliente até o submit(); aí vai para /api/avocado/discussion-hero e
    // o model local é atualizado, para a discussão já abrir com ela.
    _defineProperty(this, "heroImageFile", null);
    _defineProperty(this, "heroImagePreview", null);
    // Campos do changelog (só usados quando uma tag escolhida é um produto): o
    // número da versão e o banner na cor da tag, que vale na ausência de imagem.
    _defineProperty(this, "changelogVersion", '');
    /** Capa colorida, já codificada (utils/cover); 'color' = automática, null = sem capa colorida. */
    _defineProperty(this, "changelogCover", 'color');
    // fof/drafts: o rascunho salvo a partir deste compositor. O fof só cuida do
    // compositor do core (ComposerState.draft), então este guarda o seu aqui.
    _defineProperty(this, "draft", null);
    _defineProperty(this, "savingDraft", false);
    _defineProperty(this, "draftJustSaved", false);
    /** Exigido pelo TextEditor do Flarum: repassa o corpo do compositor. */
    _defineProperty(this, "composerProxy", {
      isVisible: () => true,
      fields: {
        content: () => this.body
      }
    });
  }
  isValid() {
    if (!this.title.trim() || !this.body.trim()) return false;
    if (this.tagBypassReqs) return true;
    return this.tagsMeetMinimums();
  }

  /** A seleção atende `minPrimaryTags`/`minSecondaryTags`; sempre true sem tags no fórum. */
  tagsMeetMinimums() {
    const minP = parseInt(app.forum.attribute('minPrimaryTags')) || 0;
    const minS = parseInt(app.forum.attribute('minSecondaryTags')) || 0;
    const primary = this.tags.filter(t => t.position?.() !== null && !t.isChild?.()).length;
    const secondary = this.tags.filter(t => t.position?.() === null).length;
    const selectableCount = app.store.all('tags').filter(Boolean).length;
    if (selectableCount === 0) return true;
    return primary >= minP && secondary >= minS;
  }
  addTag(tag) {
    if (this.tags.includes(tag)) return;
    const next = [...this.tags];
    const parent = tag.parent?.();
    if (parent && parent !== false && !next.includes(parent)) next.push(parent);
    next.push(tag);
    this.tags = next;
    this.tagFilter = '';
  }
  removeTag(tag) {
    this.tags = this.tags.filter(t => t !== tag && t.parent?.()?.id?.() !== tag.id?.());
  }
  setHeroImageFile(file) {
    if (this.heroImagePreview) {
      try {
        URL.revokeObjectURL(this.heroImagePreview);
      } catch {
        /* noop */
      }
    }
    this.heroImageFile = file;
    this.heroImagePreview = file ? URL.createObjectURL(file) : null;
  }
  reset() {
    this.title = '';
    this.body = '';
    this.tags = [];
    this.preview = false;
    this.submitting = false;
    this.tagBypassReqs = false;
    this.tagPickerOpen = false;
    this.tagFilter = '';
    this.setHeroImageFile(null);
    this.changelogVersion = '';
    this.changelogCover = 'color';
    this.draft = null;
    this.savingDraft = false;
    this.draftJustSaved = false;
  }

  /** `canSaveDrafts` só existe com o fof/drafts ativo e quem tem a permissão. */
  canSaveDraft() {
    return !!app.forum.attribute('canSaveDrafts');
  }

  /**
   * Salva (ou atualiza) o rascunho no mesmo formato que o compositor do core
   * grava: título, conteúdo, tags em `relationships` e os campos do changelog
   * em `extra` — assim abrir o rascunho pelo menu do fof ou publicá-lo agendado
   * funciona igual. A imagem do hero fica de fora: só existe no navegador.
   */
  saveDraft() {
    if (this.savingDraft || !this.canSaveDraft() || !this.body.trim()) return Promise.resolve();
    this.savingDraft = true;
    m.redraw();
    const payload = {
      title: this.title,
      content: this.body,
      relationships: {
        tags: {
          data: this.tags.map(tag => ({
            type: 'tags',
            id: tag.id()
          }))
        }
      },
      extra: tagsAreChangelogProducts(this.tags) ? changelogAttributes(this.changelogVersion, this.changelogCover, !!this.heroImageFile) : {}
    };
    const isNew = !this.draft;
    const request = isNew ? app.store.createRecord('drafts').save(payload) : this.draft.save(payload);
    return request.then(draft => {
      // O contador sobe sozinho: a resposta do create já traz o usuário com o `draftCount` novo.
      if (isNew) this.draft = draft;
      this.draftJustSaved = true;
      setTimeout(() => {
        this.draftJustSaved = false;
        m.redraw();
      }, 300);
    }).catch(err => console.error('Draft save failed:', err)).finally(() => {
      this.savingDraft = false;
      m.redraw();
    });
  }

  /** Publicada a discussão, o rascunho de onde ela veio sai da lista (como no core). */
  discardDraft() {
    const draft = this.draft;
    this.draft = null;
    if (!draft?.exists) return;
    draft.delete().then(() => {
      adjustDraftCount(-1);
      m.redraw();
    });
  }

  /**
   * Envia a discussão e devolve a salva. Rejeita sem mexer no estado se a
   * validação falhar.
   */
  submit() {
    if (this.submitting) return Promise.reject(new Error('already submitting'));
    if (!this.title.trim() || !this.body.trim()) return Promise.reject(new Error('empty fields'));
    if (!this.tagBypassReqs && !this.tagsMeetMinimums()) {
      this.tagPickerOpen = true;
      m.redraw();
      return Promise.reject(new Error('tags below minimum'));
    }
    this.submitting = true;
    m.redraw();
    const data = {
      title: this.title.trim(),
      content: this.body.trim()
    };
    if (this.tags.length > 0) data.relationships = {
      tags: this.tags
    };
    if (tagsAreChangelogProducts(this.tags)) {
      Object.assign(data, changelogAttributes(this.changelogVersion, this.changelogCover, !!this.heroImageFile));
    }
    return app.store.createRecord('discussions').save(data).then(async discussion => {
      // Falha no upload da imagem não desfaz a discussão: ela fica sem imagem e um
      // alerta deixa o usuário tentar de novo na página da discussão.
      if (this.heroImageFile) {
        try {
          const result = await uploadDiscussionHeroImage(discussion.id(), this.heroImageFile);
          // Atualiza o store local para a discussão já abrir com a imagem.
          const data = discussion.data;
          if (data?.attributes) {
            data.attributes.heroImagePath = result.heroImagePath;
            data.attributes.heroImageUrl = result.heroImageUrl;
          }
        } catch (err) {
          try {
            app.alerts.show({
              type: 'error'
            }, trans('ramon-avocado.forum.home.composer_hero_image_upload_failed', 'Could not upload the hero image. You can try again on the discussion page.'));
          } catch {
            /* alertas podem não existir */
          }
        }
      }
      this.discardDraft();
      this.submitting = false;
      return discussion;
    }).catch(err => {
      this.submitting = false;
      m.redraw();
      throw err;
    });
  }
}
flarum.reg.add('ramon-avocado', 'forum/states/InlineComposerState', InlineComposerState);